import type { OpportunityDetails } from '../../lib/types'

/** Share-priced deals (CB-239). All money is integer cents so fees round half-up exactly. */
export const MIN_FINAL_INVESTMENT_CENTS = 10_000

export const BANK_CHARGES_NOTICE =
  'This amount is gross of bank charges. When you send the wire transfer, choose the option where all bank charges are paid by the sender (charge code “OUR”), so that the full final investment amount is received.'

export type SharePricing = {
  pricePerShareCents: number
  /** Fee percent x 100 (5% => 500). */
  salesFeeBasisPoints: number
  /** Minimum final investment amount (incl. fees): the larger of $100 and the deal's ticket size. */
  minFinalCents: number
}

export type ShareOrder = {
  shares: number
  amountCents: number
  salesFeeCents: number
  finalAmountCents: number
}

const toCents = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null
}

/** Reads price per share and sales fee from opportunity metadata; null for amount-only deals. */
export function getSharePricing(opportunity: OpportunityDetails | undefined): SharePricing | null {
  const meta = opportunity?.metadata as
    | { price_per_share?: string; sales_fee_percent?: string }
    | undefined
  const pricePerShareCents = toCents(meta?.price_per_share)
  if (!pricePerShareCents) return null
  const ticketCents = Number(opportunity?.ticketSize ?? 0)
  return {
    pricePerShareCents,
    salesFeeBasisPoints: toCents(meta?.sales_fee_percent) ?? 0,
    minFinalCents: Math.max(
      MIN_FINAL_INVESTMENT_CENTS,
      Number.isFinite(ticketCents) ? ticketCents : 0,
    ),
  }
}

export function computeShareOrder(shares: number, pricing: SharePricing): ShareOrder {
  const amountCents = shares * pricing.pricePerShareCents
  const salesFeeCents = Math.floor((amountCents * pricing.salesFeeBasisPoints + 5_000) / 10_000)
  return { shares, amountCents, salesFeeCents, finalAmountCents: amountCents + salesFeeCents }
}

/** Whole shares purchasable with an amount in USD (rounded down). */
export function sharesFromAmountUsd(amountUsd: number, pricing: SharePricing): number {
  if (!Number.isFinite(amountUsd) || amountUsd <= 0) return 0
  return Math.floor(Math.round(amountUsd * 100) / pricing.pricePerShareCents)
}

export function minSharesForMinimum(pricing: SharePricing): number {
  // Start below the answer (fee < 100%) and walk up to the first order that clears the minimum.
  let shares = Math.max(1, Math.floor(pricing.minFinalCents / (pricing.pricePerShareCents * 2)))
  while (computeShareOrder(shares, pricing).finalAmountCents < pricing.minFinalCents) {
    shares += 1
  }
  return shares
}

export function formatCentsUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Error text when the final amount is under the minimum; null when the order is acceptable. */
export function getMinimumFinalAmountError(
  order: ShareOrder,
  pricing: SharePricing,
): string | null {
  if (order.finalAmountCents >= pricing.minFinalCents) return null
  return `Final Investment amount must be at least $${formatCentsUsd(pricing.minFinalCents).replace(
    /\.00$/,
    '',
  )}.`
}

export type ShareCalculatorChange = {
  shares: number
  /** Investment amount before fees, in USD. */
  amountUsd: number
  isValid: boolean
}
